/**
 * How orders find a driver.
 *
 * Every number here has a consequence that is not obvious from its name, so each setting
 * carries the sentence that says what it does and what it is measured in. An offer timeout
 * longer than the search timeout does something surprising; a retry delay of ten seconds
 * trains passengers to hammer the button.
 *
 * Whether this screen may be used at all is decided by asking for the settings and seeing
 * whether the server allows it. The client deliberately does not re-implement the policy —
 * the API is the authority on who may configure a firm, and a second copy of that rule in
 * TypeScript is a copy that will eventually disagree.
 *
 * One request saves the lot, so the page carries the `SaveBar` rather than a Save button per
 * card: the draft is edited freely and the bar appears the moment it differs from what was
 * loaded.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  dispatchLimits,
  dispatchStages,
  noCarsActions,
  settingsApi,
  type DispatchSettings,
  type DispatchStage,
} from '../api';
import { refusal } from '../errors';
import { checkForm, named } from '../check';
import { useListText } from '../listText';
import {
  Banner,
  Button,
  EmptyState,
  FieldErrors,
  Flagged,
  Icon,
  LadderRow,
  Measured,
  PageHeader,
  SaveBar,
  Section,
  Select,
  SettingRow,
  Switch,
  type FieldProblem,
} from '../../ui';
import { forgetPreBookingsSetting } from '../../orders/preBookingsSetting';

export function DispatchPage() {
  const { t } = useTranslation();
  // What `checkForm` reads before a save: every control on the page, at once (#377).
  const page = useRef<HTMLDivElement>(null);

  /** What the server last told us, which Discard returns to and the change count is read against. */
  const [loaded, setLoaded] = useState<DispatchSettings | null>(null);
  const [draft, setDraft] = useState<DispatchSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<FieldProblem | null>(null);
  const [saved, setSaved] = useState(false);

  const [radiiText, setRadiiText] = useListText(draft?.dispatchRadiiMetres ?? [], parseRadii);

  useEffect(() => {
    void (async () => {
      try {
        const settings = await settingsApi.dispatch();

        setLoaded(settings);
        setDraft(settings);
      } catch (failure) {
        setError(refusal(failure, t, 'settings.couldNotLoad'));
      }
    })();
  }, [t]);

  // The confirmation is a moment, not a state: it says the save landed and then gets out of
  // the way, rather than sitting there until the next edit and being read as "this is saved"
  // by somebody who has since changed something.
  useEffect(() => {
    if (!saved) return;

    const timer = globalThis.setTimeout(() => setSaved(false), 4000);

    return () => globalThis.clearTimeout(timer);
  }, [saved]);

  const change = useCallback(
    <K extends keyof DispatchSettings>(key: K, value: DispatchSettings[K]) => {
      setSaved(false);
      setDraft((current) => (current === null ? current : { ...current, [key]: value }));
    },
    [],
  );

  /** How many settings differ from what was loaded — the number the save bar counts. */
  const changes = useMemo(() => {
    if (loaded === null || draft === null) return 0;

    return (Object.keys(loaded) as (keyof DispatchSettings)[]).filter(
      (key) => !same(loaded[key], draft[key]),
    ).length;
  }, [loaded, draft]);

  async function save() {
    if (draft === null) return;

    const invalid = checkForm(page.current, t);

    if (invalid !== null) {
      setError(invalid);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Replaced with what the server stored, not with what was typed. If it clamped or
      // corrected anything, the form should show the truth rather than the request.
      const stored = await settingsApi.saveDispatch(draft);

      // The order form asks once per session whether to show "For later"; it is told again.
      forgetPreBookingsSetting();

      setLoaded(stored);
      setDraft(stored);
      setSaved(true);
    } catch (failure) {
      setError(named(refusal(failure, t, 'settings.couldNotSave'), page.current, t));
    } finally {
      setSaving(false);
    }
  }

  const tellingTheRider = draft !== null && draft.whenNoCarsFound === 'TellTheRider';

  return (
    <FieldErrors root={page} problem={error} onEdit={() => setError(null)}>
      <PageHeader
        title={t('nav.settings.dispatch')}
        description={t('nav.settingsAbout.dispatch')}
      />

      {error !== null && <Banner tone="danger">{error.message}</Banner>}
      {saved && <Banner tone="ok">{t('settings.saved')}</Banner>}

      {draft === null ? (
        error === null && <EmptyState>{t('app.loading')}</EmptyState>
      ) : (
        <form
          noValidate
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <Section title={t('settings.groupSending')} description={t('settings.groupSendingHelp')}>
            <SettingRow label={t('settings.autoDispatch')} help={t('settings.autoDispatchHelp')}>
              <Switch
                checked={draft.autoDispatchEnabled}
                label={t('settings.autoDispatch')}
                onChange={(next) => change('autoDispatchEnabled', next)}
              />
            </SettingRow>

            <NumberSetting
              id="dispatchIntervalSeconds"
              value={draft.dispatchIntervalSeconds}
              limits={dispatchLimits.dispatchIntervalSeconds}
              onChange={(v) => change('dispatchIntervalSeconds', v)}
            />

            <NumberSetting
              id="offerTimeoutSeconds"
              value={draft.offerTimeoutSeconds}
              limits={dispatchLimits.offerTimeoutSeconds}
              onChange={(v) => change('offerTimeoutSeconds', v)}
            />

            <NumberSetting
              id="maxDispatchAttempts"
              value={draft.maxDispatchAttempts}
              limits={dispatchLimits.maxDispatchAttempts}
              unit="settings.unitDrivers"
              onChange={(v) => change('maxDispatchAttempts', v)}
            />

            <NumberSetting
              id="searchTimeoutSeconds"
              value={draft.searchTimeoutSeconds}
              limits={dispatchLimits.searchTimeoutSeconds}
              onChange={(v) => change('searchTimeoutSeconds', v)}
            />

            <SettingRow
              label={t('settings.whenNoCarsFound')}
              help={t('settings.whenNoCarsFoundHelp')}
              last={!tellingTheRider}
            >
              <Select
                id="whenNoCarsFound"
                aria-label={t('settings.whenNoCarsFound')}
                value={draft.whenNoCarsFound}
                onChange={(e) =>
                  change('whenNoCarsFound', e.target.value as DispatchSettings['whenNoCarsFound'])
                }
              >
                {noCarsActions.map((action) => (
                  <option key={action} value={action}>
                    {t(`settings.noCarsAction.${action}`)}
                  </option>
                ))}
              </Select>
            </SettingRow>

            {/* Only means anything under TellTheRider, so it is hidden rather than shown
                greyed out — a disabled field still invites somebody to wonder what it does. */}
            {tellingTheRider && (
              <NumberSetting
                id="retryAfterSeconds"
                value={draft.retryAfterSeconds}
                limits={dispatchLimits.retryAfterSeconds}
                last
                onChange={(v) => change('retryAfterSeconds', v)}
              />
            )}
          </Section>

          <Section title={t('settings.groupLadder')} description={t('settings.groupLadderHelp')}>
            {/* Not a control, so the name a refusal uses is given to the block around it. */}
            <Flagged id="dispatchStages">
              <Ladder
                stages={draft.dispatchStages}
                onChange={(stages) => change('dispatchStages', stages)}
              />
            </Flagged>

            <SettingRow
              label={t('settings.dispatchRadiiMetres')}
              help={t('settings.dispatchRadiiMetresHelp')}
              last
            >
              {/* The one number box holding a list rather than a number: wider, and read
                  left to right, but keeping the primitive's unit gutter so the row still
                  lines up with every other unit on the page. */}
              <Measured
                wide
                id="dispatchRadiiMetres"
                type="text"
                inputMode="numeric"
                aria-label={t('settings.dispatchRadiiMetres')}
                unit={t('settings.unitMetres')}
                value={radiiText}
                onChange={(e) => {
                  setRadiiText(e.target.value);
                  change('dispatchRadiiMetres', parseRadii(e.target.value));
                }}
              />
            </SettingRow>
          </Section>

          <Section title={t('settings.groupRanks')} description={t('settings.groupRanksHelp')}>
            <NumberSetting
              id="rankFirstOfferSeconds"
              value={draft.rankFirstOfferSeconds}
              limits={dispatchLimits.rankFirstOfferSeconds}
              onChange={(v) => change('rankFirstOfferSeconds', v)}
            />

            <NumberSetting
              id="rankRestOfferSeconds"
              value={draft.rankRestOfferSeconds}
              limits={dispatchLimits.rankRestOfferSeconds}
              onChange={(v) => change('rankRestOfferSeconds', v)}
            />

            <NumberSetting
              id="rankExitMarginMetres"
              value={draft.rankExitMarginMetres}
              limits={dispatchLimits.rankExitMarginMetres}
              unit="settings.unitMetres"
              last
              onChange={(v) => change('rankExitMarginMetres', v)}
            />
          </Section>

          <Section
            title={t('settings.groupFairness')}
            description={t('settings.groupFairnessHelp')}
          >
            <NumberSetting
              id="acceptancePenaltyMetres"
              value={draft.acceptancePenaltyMetres}
              limits={dispatchLimits.acceptancePenaltyMetres}
              unit="settings.unitMetres"
              onChange={(v) => change('acceptancePenaltyMetres', v)}
            />

            <NumberSetting
              id="idleBonusMetresPerMinute"
              value={draft.idleBonusMetresPerMinute}
              limits={dispatchLimits.idleBonusMetresPerMinute}
              unit="settings.unitMetresPerMinute"
              onChange={(v) => change('idleBonusMetresPerMinute', v)}
            />

            <NumberSetting
              id="maxFairnessAdjustmentMetres"
              value={draft.maxFairnessAdjustmentMetres}
              limits={dispatchLimits.maxFairnessAdjustmentMetres}
              unit="settings.unitMetres"
              onChange={(v) => change('maxFairnessAdjustmentMetres', v)}
            />

            <NumberSetting
              id="acceptanceWindowDays"
              value={draft.acceptanceWindowDays}
              limits={dispatchLimits.acceptanceWindowDays}
              unit="settings.unitDays"
              onChange={(v) => change('acceptanceWindowDays', v)}
            />

            <NumberSetting
              id="roadRankingCandidates"
              value={draft.roadRankingCandidates}
              limits={dispatchLimits.roadRankingCandidates}
              unit="settings.unitDrivers"
              last
              onChange={(v) => change('roadRankingCandidates', v)}
            />
          </Section>

          <Section
            title={t('settings.groupPreBookings')}
            description={t('settings.groupPreBookingsHelp')}
          >
            <SettingRow
              label={t('settings.preBookingsEnabled')}
              help={t(
                draft.preBookingsEnabled
                  ? 'settings.preBookingsEnabledOn'
                  : 'settings.preBookingsEnabledOff',
              )}
              last={!draft.preBookingsEnabled}
            >
              <Switch
                checked={draft.preBookingsEnabled}
                label={t('settings.preBookingsEnabled')}
                onChange={(next) => change('preBookingsEnabled', next)}
              />
            </SettingRow>

            {/* The two timings mean nothing for a firm that takes no orders for later, so they
                go with the switch rather than sit there greyed out. */}
            {draft.preBookingsEnabled && (
              <>
                <NumberSetting
                  id="preBookingLeadMinutes"
                  value={draft.preBookingLeadMinutes}
                  limits={dispatchLimits.preBookingLeadMinutes}
                  unit="settings.unitMinutes"
                  onChange={(v) => change('preBookingLeadMinutes', v)}
                />

                <NumberSetting
                  id="preBookingEscalateMinutes"
                  value={draft.preBookingEscalateMinutes}
                  limits={dispatchLimits.preBookingEscalateMinutes}
                  unit="settings.unitMinutes"
                  last
                  onChange={(v) => change('preBookingEscalateMinutes', v)}
                />
              </>
            )}
          </Section>

          <SaveBar
            changes={changes}
            problem={error?.message}
            busy={saving}
            discardLabel={t('settings.discard')}
            saveLabel={saving ? t('settings.saving') : t('settings.save')}
            onDiscard={() => {
              setSaved(false);
              setError(null);
              setDraft(loaded);
            }}
            onSave={() => void save()}
          >
            {t('settings.unsaved', { count: changes })}
          </SaveBar>
        </form>
      )}
    </FieldErrors>
  );
}

/**
 * Whether one setting is still what was loaded.
 *
 * The two lists — the ladder and the radii — are compared by their contents rather than by
 * identity, because every keystroke in the radii box builds a new array and a bar reading
 * "1 change not saved" for a value nobody changed is a bar nobody believes.
 */
function same(before: unknown, after: unknown): boolean {
  return Array.isArray(before) && Array.isArray(after)
    ? before.length === after.length && before.every((value, index) => value === after[index])
    : before === after;
}

/**
 * Reads a list of radii out of what somebody typed.
 *
 * Forgiving on the way in — commas, spaces, or both — and strict on the way out: anything
 * that is not a positive number is dropped rather than stored as a zero-metre circle. The
 * server sorts and deduplicates whatever arrives, so the order typed here does not matter.
 */
function parseRadii(text: string): number[] {
  return text
    .split(/[,\s]+/)
    .map((part) => globalThis.Number.parseInt(part, 10))
    .filter((value) => globalThis.Number.isFinite(value) && value > 0);
}

/**
 * The rungs of the ladder: which are used, and in what order.
 *
 * Order is editable because a firm that wants its ranks asked after its zones should not
 * have to ring anybody. A rung nobody has ticked drops below the ladder as a row offering to
 * put it back, and the API refuses an empty ladder — orders offered to nobody is not a
 * configuration anybody meant.
 */
function Ladder({
  stages,
  onChange,
}: {
  stages: DispatchStage[];
  onChange: (stages: DispatchStage[]) => void;
}) {
  const { t } = useTranslation();

  function move(from: number, by: number) {
    const to = from + by;

    if (to < 0 || to >= stages.length) return;

    const reordered = [...stages];
    [reordered[from], reordered[to]] = [reordered[to], reordered[from]];

    onChange(reordered);
  }

  function toggle(stage: DispatchStage, on: boolean) {
    onChange(on ? [...stages, stage] : stages.filter((existing) => existing !== stage));
  }

  const unused = dispatchStages.filter((stage) => !stages.includes(stage));

  return (
    <>
      {stages.map((stage, index) => (
        <LadderRow
          key={stage}
          index={index + 1}
          actions={
            <>
              <Button
                size="sm"
                icon
                disabled={index === 0}
                aria-label={t('settings.moveUp')}
                onClick={() => move(index, -1)}
              >
                <Icon name="arrow-up" small />
              </Button>

              <Button
                size="sm"
                icon
                disabled={index === stages.length - 1}
                aria-label={t('settings.moveDown')}
                onClick={() => move(index, 1)}
              >
                <Icon name="arrow-down" small />
              </Button>

              <Button
                size="sm"
                icon
                aria-label={t('settings.removeStage')}
                onClick={() => toggle(stage, false)}
              >
                <Icon name="close" small />
              </Button>
            </>
          }
        >
          <div className="text-body font-medium">{t(`settings.stage.${stage}`)}</div>
          <div className="text-secondary text-ink-3 mt-0.5 max-w-[58ch] leading-[1.4]">
            {t(`settings.stage.${stage}Help`)}
          </div>
        </LadderRow>
      ))}

      {unused.map((stage) => (
        <SettingRow
          key={stage}
          label={t(`settings.stage.${stage}`)}
          help={t(`settings.stage.${stage}Help`)}
        >
          <Button size="sm" onClick={() => toggle(stage, true)}>
            {t('settings.addStage')}
          </Button>
        </SettingRow>
      ))}
    </>
  );
}

/**
 * One numeric setting, with its unit and an explanation.
 *
 * The label, the help text and the unit all come from the setting's own name, so adding a
 * setting is a translation key rather than another block of markup — and forgetting the
 * explanation shows up as a visible missing key rather than as a field nobody understands.
 */
function NumberSetting({
  id,
  value,
  limits,
  unit = 'settings.unitSeconds',
  last,
  onChange,
}: {
  id: string;
  value: number;
  limits: { min: number; max: number };
  unit?: string;
  last?: boolean;
  onChange: (value: number) => void;
}) {
  const { t } = useTranslation();

  return (
    <SettingRow label={t(`settings.${id}`)} help={t(`settings.${id}Help`)} last={last}>
      <Measured
        id={id}
        unit={t(unit)}
        type="number"
        inputMode="numeric"
        aria-label={t(`settings.${id}`)}
        required
        value={Number.isNaN(value) ? '' : value}
        min={limits.min}
        max={limits.max}
        onChange={(e) => onChange(e.target.valueAsNumber)}
      />
    </SettingRow>
  );
}
